/**
 * Utility functions for authentication
 */

interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  shouldRetry?: (error: any) => boolean;
}

/**
 * Exponential backoff retry wrapper for async functions
 */
export async function withExponentialBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    shouldRetry = (error) => {
      // Retry on network errors or 5xx server errors
      if (error?.status >= 500 && error?.status < 600) return true;
      if (error?.code === 'NETWORK_ERROR') return true;
      if (error?.message?.includes('network')) return true;
      if (error?.message?.includes('timeout')) return true;
      return false;
    }
  } = options;

  let lastError: any;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries || !shouldRetry(error)) {
        console.error(`❌ Failed after ${attempt + 1} attempts:`, error);
        throw error;
      }

      console.warn(`⚠️ Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Calculate next delay with exponential backoff
      delay = Math.min(delay * backoffFactor, maxDelay);
    }
  }

  throw lastError;
}

/**
 * Check if the error is a network error
 */
export function isNetworkError(error: any): boolean {
  return (
    error?.code === 'NETWORK_ERROR' ||
    error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('fetch') ||
    error?.message?.toLowerCase().includes('timeout') ||
    !navigator.onLine
  );
}

/**
 * Check if the error is an authentication error
 */
export function isAuthError(error: any): boolean {
  return (
    error?.status === 401 ||
    error?.code === 'UNAUTHORIZED' ||
    error?.message?.toLowerCase().includes('unauthorized') ||
    error?.message?.toLowerCase().includes('authentication') ||
    error?.message?.toLowerCase().includes('invalid token')
  );
}

/**
 * Get a user-friendly error message
 */
export function getAuthErrorMessage(error: any): string {
  if (isNetworkError(error)) {
    return 'Network connection error. Please check your internet connection.';
  }
  
  if (isAuthError(error)) {
    return 'Authentication failed. Please sign in again.';
  }
  
  if (error?.message?.includes('email not confirmed')) {
    return 'Please verify your email address before signing in.';
  }
  
  if (error?.message?.includes('invalid login credentials')) {
    return 'Invalid email or password. Please try again.';
  }
  
  if (error?.message?.includes('user already registered') || 
      error?.message?.includes('already exists')) {
    return 'An account with this email already exists. Please try signing in instead.';
  }
  
  return error?.message || 'An unexpected error occurred. Please try again.';
}

/**
 * Session validation helper
 */
export async function validateSession(session: any): Promise<boolean> {
  if (!session || !session.access_token) {
    return false;
  }

  // Check if session is expired
  if (session.expires_at) {
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    
    if (now >= expiresAt) {
      console.log('⏰ Session expired');
      return false;
    }
  }

  return true;
}